import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestHeaders,
} from 'axios';
import { GymSpaceConfig, RequestOptions } from './types';
import {
  GymSpaceError,
  AuthenticationError,
  AuthorizationError,
  NotFoundError,
  ValidationError,
  NetworkError,
} from './errors';

export class ApiClient {
  private axiosInstance: AxiosInstance;
  private config: GymSpaceConfig;
  private refreshToken: string | null = null;
  private onTokenRefreshed?: (accessToken: string, refreshToken?: string) => void;

  public getAccessToken(): string | null {
    return this.config.apiKey || null;
  }

  constructor(config: GymSpaceConfig) {
    this.config = config;

    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
      paramsSerializer: (params) => {
        const parts: string[] = [];

        Object.entries(params).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            return;
          }

          // Convert arrays to comma-separated strings
          if (Array.isArray(value)) {
            if (value.length > 0) {
              // Encode each array element individually, then join with comma
              const encodedValues = value.map((v) => encodeURIComponent(String(v))).join(',');
              parts.push(`${encodeURIComponent(key)}=${encodedValues}`);
            }
          } else {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
          }
        });

        return parts.join('&');
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Check if this request should skip auth
        const skipAuth = (config as any).skipAuth;

        // Add Authorization header if auth token is configured and not skipped
        if (this.config.apiKey && config.headers && !skipAuth) {
          config.headers['Authorization'] = `Bearer ${this.config.apiKey}`;
        }

        // Add refresh token header for ALL authenticated requests (not skipped)
        if (this.refreshToken && this.config.apiKey && config.headers && !skipAuth) {
          config.headers['X-Refresh-Token'] = this.refreshToken;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor - handle token refresh from backend
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // Check if backend refreshed tokens
        const newAccessToken = response.headers['x-new-access-token'];
        const newRefreshToken = response.headers['x-new-refresh-token'];

        if (newAccessToken) {
          console.log('Tokens refreshed by backend, updating SDK...');
          this.setAuthToken(newAccessToken);

          if (newRefreshToken) {
            this.setRefreshToken(newRefreshToken);
          }

          // Notify client about token refresh
          if (this.onTokenRefreshed) {
            this.onTokenRefreshed(newAccessToken, newRefreshToken);
          }
        }

        return response;
      },
      async (error: AxiosError) => {
        throw this.handleError(error);
      },
    );
  }

  private handleError(error: AxiosError): GymSpaceError {
    const requestPath = error.config?.url || 'unknown';
    const method = error.config?.method?.toUpperCase() || 'unknown';

    if (!error.response) {
      console.error('Network Error:', {
        method,
        path: requestPath,
        error: error.message,
      });
      return new NetworkError(error.message);
    }

    const { status, data } = error.response;
    const errorData = data as any;
    const resource = errorData?.resource || 'unknown';

    console.error('HTTP Error:', {
      method,
      path: requestPath,
      status,
      resource,
      message: errorData?.message || error.message,
      errorCode: errorData?.code,
      details: errorData,
    });

    switch (status) {
      case 401:
        return new AuthenticationError(errorData?.message || 'Authentication failed');
      case 403:
        return new AuthorizationError(errorData?.message || 'Access denied');
      case 404:
        return new NotFoundError(errorData?.resource || 'Resource', errorData?.id);
      case 400:
        return new ValidationError(errorData?.message || 'Validation failed', errorData?.errors);
      default:
        return new GymSpaceError(
          errorData?.message || error.message,
          status,
          errorData?.code,
          errorData,
        );
    }
  }

  private mergeOptions(options?: RequestOptions): AxiosRequestConfig {
    const headers: Record<string, string> = { ...options?.headers };

    if (options?.gymId) {
      headers['X-Gym-Id'] = options.gymId;
    }

    return { headers };
  }

  async request<T>(
    method: string,
    path: string,
    data?: any,
    options?: RequestOptions & AxiosRequestConfig,
  ): Promise<T> {
    const { headers, skipAuth, ...axiosConfig } = options || {};

    const config: AxiosRequestConfig & { skipAuth?: boolean } = {
      method,
      url: path,
      ...this.mergeOptions({ headers }),
      ...axiosConfig,
      skipAuth, // Pass skipAuth flag to interceptor
    };

    if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
      config.data = data;
    } else if (data && method.toUpperCase() === 'GET') {
      config.params = data;
    }

    const response = await this.axiosInstance.request<T>(config);
    return response.data;
  }

  get<T>(path: string, params?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', path, params, options);
  }

  post<T>(path: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', path, data, options);
  }

  put<T>(path: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', path, data, options);
  }

  patch<T>(path: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', path, data, options);
  }

  delete<T>(path: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', path, undefined, options);
  }

  setAuthToken(token: string): void {
    this.config.apiKey = token;
  }

  setRefreshToken(token: string | null): void {
    this.refreshToken = token;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  /**
   * Set callback for when tokens are refreshed by backend
   */
  setOnTokenRefreshed(callback: (accessToken: string, refreshToken?: string) => void): void {
    this.onTokenRefreshed = callback;
  }

  /**
   * Get the token refresh callback
   */
  getOnTokenRefreshed(): ((accessToken: string, refreshToken?: string) => void) | undefined {
    return this.onTokenRefreshed;
  }

  setGymId(gymId: string): void {
    this.axiosInstance.defaults.headers.common['X-Gym-Id'] = gymId;
  }

  clearAuth(): void {
    delete this.config.apiKey;
    this.refreshToken = null;
    delete this.axiosInstance.defaults.headers.common['Authorization'];
    delete this.axiosInstance.defaults.headers.common['X-Gym-Id'];
  }

  getBaseUrl(): string {
    return this.config.baseURL;
  }
}
